import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ChevronRight, X } from "lucide-react";
import type { ServiceOccurrence } from "@/types/service";

const focusClass = "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-400";
const time = (value: string) => new Date(value).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

export function ServiceCalendarEntry({ service, contextName }: { service: ServiceOccurrence; contextName: string }) {
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const id = useId();
  const cancelled = service.status === "CANCELLED";
  const date = new Date(service.starts_at).toLocaleDateString(undefined, { dateStyle: "full" });
  function close(restoreFocus = true) {
    setPosition(null);
    if (restoreFocus) trigger.current?.focus();
  }
  function locate() {
    const rect = trigger.current!.getBoundingClientRect();
    const width = Math.min(320, window.innerWidth - 24);
    // Keep the compact surface beside the event where space permits, and above it near the bottom.
    const height = Math.min(panel.current?.offsetHeight || 300, window.innerHeight - 24);
    return {
      left: Math.max(12, Math.min(rect.left, window.innerWidth - width - 12)),
      top: Math.max(12, Math.min(rect.bottom + 8, window.innerHeight - height - 12)),
    };
  }
  useEffect(() => {
    if (!position) return;
    panel.current?.focus();
    function keydown(event: KeyboardEvent) {
      if (event.key === "Escape") { event.preventDefault(); setPosition(null); trigger.current?.focus(); }
    }
    function outside(event: PointerEvent) {
      const target = event.target as Node;
      if (!panel.current?.contains(target) && !trigger.current?.contains(target)) setPosition(null);
    }
    function focusOutside(event: FocusEvent) {
      const target = event.target as Node;
      if (!panel.current?.contains(target) && !trigger.current?.contains(target)) setPosition(null);
    }
    // Close on viewport/scroll changes so the surface never loses its event anchor.
    function moved(event: Event) {
      if (panel.current?.contains(event.target as Node)) return;
      setPosition(null);
    }
    document.addEventListener("keydown", keydown);
    document.addEventListener("pointerdown", outside);
    document.addEventListener("focusin", focusOutside);
    window.addEventListener("resize", moved);
    window.addEventListener("scroll", moved, true);
    return () => {
      document.removeEventListener("keydown", keydown);
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("focusin", focusOutside);
      window.removeEventListener("resize", moved);
      window.removeEventListener("scroll", moved, true);
    };
  }, [position]);

  return <>
    <button ref={trigger} type="button" aria-label={`View details for ${service.title}, ${date}, ${time(service.starts_at)}, ${contextName}, ${service.status.toLowerCase()}`}
      aria-expanded={!!position} aria-controls={position ? id : undefined} aria-haspopup="dialog"
      onClick={() => position ? close() : setPosition(locate())}
      className={`block min-h-11 w-full cursor-pointer rounded-lg border p-2 text-left text-xs transition hover:border-indigo-400 hover:bg-slate-800 ${focusClass} ${cancelled ? "border-rose-500/30 bg-rose-500/5" : "border-indigo-500/30 bg-indigo-500/10"}`}>
      <span className="flex items-start justify-between gap-1 font-semibold text-white"><span className="break-words">{service.title}</span><ChevronRight size={14} className="shrink-0 text-slate-300" aria-hidden="true" /></span>
      <span className="mt-1 block text-indigo-200">{contextName}</span>
      <span className="mt-1 block text-slate-300">{time(service.starts_at)}</span>
      <span className={`mt-1 block capitalize ${cancelled ? "text-rose-300" : "text-slate-300"}`}>{service.status.toLowerCase()}</span>
    </button>
    {position && createPortal(<div ref={panel} id={id} role="dialog" aria-labelledby={`${id}-title`} tabIndex={-1}
      style={{ left: position.left, top: position.top, width: "min(320px, calc(100vw - 24px))", maxHeight: `calc(100dvh - ${position.top + 12}px)` }}
      className={`fixed z-50 overflow-y-auto rounded-2xl border border-slate-600 bg-slate-950 p-5 text-sm text-slate-200 shadow-2xl shadow-black/50 ${focusClass}`}>
      <div className="flex items-start justify-between gap-3">
        <h3 id={`${id}-title`} className="break-words text-lg font-semibold text-white">{service.title}</h3>
        <button type="button" onClick={() => close()} aria-label="Close service details" className={`-mr-2 -mt-2 flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-xl text-slate-300 hover:bg-slate-800 hover:text-white ${focusClass}`}><X size={20} aria-hidden="true" /></button>
      </div>
      <p className="mt-2 font-medium text-indigo-200">{contextName}</p>
      <p className="mt-4 font-medium text-white">{date}</p>
      <p className="mt-1">{time(service.starts_at)}{service.ends_at ? ` – ${time(service.ends_at)}` : ""}</p>
      {service.location && <p className="mt-2 break-words">Location: {service.location}</p>}
      <p className={`mt-3 capitalize ${cancelled ? "text-rose-300" : "text-slate-300"}`}>Status: {service.status.toLowerCase()}</p>
      <Link to={`/services/${service.id}`} className={`mt-4 flex min-h-11 items-center justify-center rounded-xl border border-indigo-400 bg-indigo-500/15 px-4 font-medium text-white hover:bg-indigo-500/30 ${focusClass}`}>Open service</Link>
    </div>, document.body)}
  </>;
}
